import React, { useState, useEffect, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem } from "@mui/material";
import {
  fetchSkillSheetBankItem,
  createSkillSheetBankItem,
  updateSkillSheetBankItem,
  publishSkillSheetBankItem,
  deleteSkillSheetBankItem,
  fetchSkillSheetBankAssignments,
  fetchSkillSheetBankVersions,
  unassignSkillSheetFromCourse,
  assignSkillSheetToCourse,
  fetchClasses,
} from "../../api/backend";
import { useTenant } from "../../contexts/TenantContext";
import { useConfigList } from "../../hooks/useConfigList";
import SkillSheetBuilderModal from "../../components/SkillSheetBuilderModal";
import { itemPoints } from "../../components/checklistScoring";

const DEFAULT_CATEGORIES = ["Firefighter","EMS","Hazmat","Search & Rescue","Fire Officer","General"];

// ── Design tokens ─────────────────────────────────────────────────────────────
const D = {
  ink: "var(--color-ink)", ink2: "rgba(31,22,51,0.75)", ink3: "rgba(31,22,51,0.6)", ink4: "rgba(31,22,51,0.5)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-canvas-light)", surface2: "var(--color-surface-press-light)", surface3: "var(--color-surface-press-stronger)",
  primary: "var(--color-primary)", primary50: "color-mix(in srgb, var(--color-primary) 10%, transparent)", primary10: "color-mix(in srgb, var(--color-primary) 6%, transparent)", primary700: "color-mix(in srgb, var(--color-primary) 85%, black)",
  green: "var(--color-success)", green50: "var(--color-success-bg)", green100: "var(--color-success-bg)",
  amber: "var(--color-warn)", amber50: "var(--color-warn-bg)", amber100: "var(--color-warn-bg)",
  live: "#c23d68", live50: "color-mix(in srgb, var(--color-accent-pink) 10%, transparent)", live100: "color-mix(in srgb, var(--color-accent-pink) 22%, transparent)",
  danger: "var(--color-danger)", danger50: "var(--color-danger-bg)", danger100: "var(--color-danger-bg)",
  blue: "var(--color-primary)", blue50: "rgba(37,99,235,.10)",
  purple: "var(--color-accent-violet-deep)", purple50: "color-mix(in srgb, var(--color-accent-violet-deep) 10%, transparent)",
  shadowSm: "var(--shadow-card-sm)",
  shadowMd: "var(--shadow-card-md)",
};

function useD() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return {
    ...D,
    primary,
    primary700: theme.palette.primary.dark,
    primary50: alpha(primary, 0.08),
    primary10: alpha(primary, 0.06),
  };
}

const CAT_COLOR = {
  firefighter: { bg: D.live50, color: D.live },
  ems:         { bg: D.blue50, color: D.blue },
  hazmat:      { bg: D.amber50, color: D.amber },
  rescue:      { bg: D.green50, color: D.green },
  officer:     { bg: D.purple50, color: D.purple },
};

function getIconStyle(tags = [], tPrimary = "var(--color-primary)") {
  const t = tags.map(s => s.toLowerCase()).join(" ");
  for (const [k, v] of Object.entries(CAT_COLOR)) if (t.includes(k)) return v;
  return { bg: alpha(tPrimary, 0.06), color: tPrimary };
}

function parseTags(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
}

function parseItems(raw = "[]") {
  try { const v = typeof raw === "string" ? JSON.parse(raw) : raw; return Array.isArray(v) ? v : []; } catch { return []; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
}

// ── SVG helpers ───────────────────────────────────────────────────────────────
function Ico({ size = 15, sw = 2, children, style }) {
  return <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style}>{children}</Box>;
}

// ── Primitives ────────────────────────────────────────────────────────────────
function Btn({ children, primary, secondary, ghost, danger, sm, block, onClick, disabled, icon }) {
  const D = useD();
  const h = sm ? 36 : 40;
  const base = { height: h, px: sm ? "13px" : "17px", borderRadius: sm ? "9px" : "11px", border: 0, fontFamily: "inherit", fontSize: sm ? "13px" : "13.5px", fontWeight: 700, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "8px", transition: "all .13s", whiteSpace: "nowrap", width: block ? "100%" : undefined, justifyContent: block ? "center" : undefined };
  const v = primary   ? { bgcolor: disabled ? D.surface3 : D.primary, color: disabled ? D.ink3 : "var(--color-surface-canvas-light)", boxShadow: `0 4px 12px -4px ${alpha(D.primary, 0.45)}`, "&:hover:not(:disabled)": { bgcolor: D.primary700 } }
    : danger    ? { bgcolor: D.danger50, color: D.danger, border: `1.5px solid ${D.danger100}`, "&:hover": { bgcolor: D.danger100 } }
    : secondary ? { bgcolor: "var(--color-surface-canvas-light)", color: D.ink2, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3, borderColor: "var(--color-hairline-cool)" } }
    :             { bgcolor: "transparent", color: D.ink3, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3 } };
  return <Box component="button" onClick={onClick} disabled={disabled} sx={{ ...base, ...v }}>{icon}{children}</Box>;
}

function IconBtn({ onClick, dangerHover, title, children }) {
  return (
    <Box component="button" title={title} onClick={onClick} sx={{ width: 36, height: 36, borderRadius: "9px", border: `1.5px solid ${D.line}`, bgcolor: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, transition: "all .12s", flexShrink: 0, "&:hover": dangerHover ? { bgcolor: D.danger50, color: D.danger, borderColor: D.danger100 } : { bgcolor: D.surface3, color: D.ink, borderColor: "var(--color-hairline-cool)" } }}>
      {children}
    </Box>
  );
}

function Panel({ children, highlight, lime }) {
  const D = useD();
  return (
    <Box sx={{
      bgcolor: "var(--color-surface-canvas-light)",
      border: lime ? "1.5px solid var(--color-lime-18)" : highlight ? `1.5px solid ${D.primary50}` : `1px solid ${D.line}`,
      borderRadius: "16px",
      boxShadow: highlight ? `0 8px 26px -14px ${alpha(D.primary, 0.34)}` : D.shadowSm,
      overflow: "hidden",
    }}>
      {children}
    </Box>
  );
}

function PanelHead({ icon, iconBg, iconColor, title, badge, action, tint }) {
  const D = useD();
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "16px 20px 14px", borderBottom: `1px solid ${tint ? "var(--color-lime-18)" : D.line2}`, bgcolor: tint ? "var(--color-lime-18)" : "transparent" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
        <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: iconBg || D.primary10, color: iconColor || D.primary, flexShrink: 0 }}>{icon}</Box>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, letterSpacing: "-.01em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>{title}</Typography>
        {badge != null && <Box sx={{ fontSize: "11px", fontWeight: 700, px: "8px", py: "2px", borderRadius: "7px", bgcolor: D.surface3, color: D.ink3 }}>{badge}</Box>}
      </Box>
      {action}
    </Box>
  );
}

function Tag({ label }) {
  const t = label.toLowerCase();
  const cat = Object.entries(CAT_COLOR).find(([k]) => t.includes(k));
  if (cat) return <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".02em", px: "10px", py: "3px", borderRadius: "999px", bgcolor: cat[1].bg, color: cat[1].color, border: `1px solid color-mix(in srgb, ${cat[1].color} 30%, transparent)` }}>{label}</Box>;
  return <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "10px", py: "3px", borderRadius: "999px", bgcolor: D.surface3, color: D.ink2, border: `1px solid ${D.line}` }}>{label}</Box>;
}

function SetRow({ label, sub, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", py: "13px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: 0 }, "&:first-of-type": { pt: 0 } }}>
      <Box>
        <Box sx={{ fontSize: "14px", fontWeight: 600, color: D.ink }}>{label}</Box>
        {sub && <Box sx={{ fontSize: "12.5px", color: D.ink3, mt: "2px" }}>{sub}</Box>}
      </Box>
      {value}
    </Box>
  );
}

function Pill({ children, green, live }) {
  return (
    <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 700, px: "11px", py: "4px", borderRadius: "999px", bgcolor: green ? D.green50 : live ? D.live50 : D.surface3, color: green ? D.green : live ? D.live : D.ink2, border: `1px solid ${green ? D.green100 : live ? D.live100 : D.line}` }}>
      {children}
    </Box>
  );
}

function MetaRow({ icon, label, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", py: "11px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: 0 }, "&:first-of-type": { pt: 0 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", fontWeight: 600, color: D.ink3 }}>
        <Box sx={{ color: D.ink4, display: "flex" }}>{icon}</Box>{label}
      </Box>
      <Box sx={{ fontSize: "13px", fontWeight: 700, color: D.ink, textAlign: "right" }}>{value}</Box>
    </Box>
  );
}

// ── Assign to Class Modal ─────────────────────────────────────────────────────
function AssignModal({ open, onClose, sheetId, alreadyAssigned, onAssigned }) {
  const D = useD();
  const { effectiveUser } = useTenant();
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !effectiveUser?.tenantId) return;
    setLoading(true);
    setSearch("");
    setSelected(new Set());
    fetchClasses(effectiveUser.tenantId)
      .then(d => { setCourses(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => { setCourses([]); setLoading(false); });
  }, [open, effectiveUser?.tenantId]);

  const assignedIds = new Set((alreadyAssigned || []).map(a => a.courseId));
  const filtered = courses.filter(c => !search || c.title?.toLowerCase().includes(search.toLowerCase()) || c.courseNumber?.toLowerCase().includes(search.toLowerCase()));

  const toggle = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const handleConfirm = async () => {
    if (!selected.size) return;
    setAssigning(true);
    setErr(null);
    try {
      await Promise.all([...selected].map(courseId => assignSkillSheetToCourse(courseId, { skillSheetId: sheetId, enabled: true, requiredForPass: false })));
      onAssigned();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to assign.");
    } finally { setAssigning(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: "13px", p: "22px 24px 18px", borderBottom: `1px solid ${D.line2}` }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "11px", bgcolor: D.primary, color: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Ico size={20}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-.02em", color: D.ink }}>Assign to Class</Box>
          <Box sx={{ fontSize: "13px", color: D.ink3, mt: "3px" }}>Select classes to add this skill sheet to.</Box>
        </Box>
        <Box component="button" onClick={onClose} sx={{ width: 32, height: 32, borderRadius: "8px", border: 0, bgcolor: D.surface3, color: D.ink3, cursor: "pointer", display: "grid", placeItems: "center", flexShrink: 0, "&:hover": { bgcolor: D.danger50, color: D.danger } }}>
          <Ico size={16}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>
        </Box>
      </DialogTitle>

      <Box sx={{ p: "14px 24px", borderBottom: `1px solid ${D.line2}` }}>
        <Box sx={{ position: "relative" }}>
          <Box sx={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", color: D.ink4, display: "flex" }}>
            <Ico size={15}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ico>
          </Box>
          <Box component="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search classes…"
            sx={{ height: 42, pl: "38px", pr: "14px", bgcolor: D.surface2, border: `1.5px solid ${D.line}`, borderRadius: "11px", fontFamily: "inherit", fontSize: "14px", color: D.ink, outline: "none", width: "100%", "&:focus": { borderColor: D.primary, boxShadow: `0 0 0 3px ${alpha(D.primary, 0.08)}`, bgcolor: "var(--color-surface-canvas-light)" } }} />
        </Box>
      </Box>

      <DialogContent sx={{ p: "10px 16px", maxHeight: 380, overflowY: "auto" }}>
        {err && <Alert severity="error" sx={{ mb: 1, borderRadius: "8px" }}>{err}</Alert>}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} sx={{ color: D.primary }} /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: D.ink4, fontSize: "13.5px" }}>No classes found.</Box>
        ) : filtered.map(c => {
          const isAssigned = assignedIds.has(c.id);
          const isSel = selected.has(c.id);
          return (
            <Box key={c.id} onClick={() => !isAssigned && toggle(c.id)}
              sx={{ display: "flex", alignItems: "center", gap: "13px", p: "11px 12px", borderRadius: "12px", cursor: isAssigned ? "default" : "pointer", opacity: isAssigned ? 0.6 : 1, transition: "background .12s", "&:hover": { bgcolor: isAssigned ? "transparent" : D.surface2 } }}>
              <Box sx={{ width: 22, height: 22, borderRadius: "7px", border: `2px solid ${isAssigned ? D.green : isSel ? D.primary : D.line}`, bgcolor: isAssigned ? D.green : isSel ? D.primary : "var(--color-surface-canvas-light)", flexShrink: 0, display: "grid", placeItems: "center", transition: "all .14s" }}>
                {(isAssigned || isSel) && <Ico size={12} sw={3} style={{ color: "var(--color-surface-canvas-light)" }}><polyline points="20 6 9 17 4 12"/></Ico>}
              </Box>
              <Box sx={{ width: 36, height: 36, borderRadius: "9px", bgcolor: D.live50, color: D.live, display: "grid", placeItems: "center", flexShrink: 0 }}>
                <Ico size={17}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Ico>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: "14px", fontWeight: 700, color: D.ink }}>{c.title}</Box>
                <Box sx={{ fontSize: "12px", color: D.ink3 }}>{c.courseNumber}{c.studentCount ? ` · ${c.studentCount} students` : ""}</Box>
              </Box>
              {isAssigned && <Box sx={{ fontSize: "11px", fontWeight: 700, color: D.green, bgcolor: D.green50, px: "9px", py: "3px", borderRadius: "999px", flexShrink: 0 }}>Assigned</Box>}
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions sx={{ p: "16px 24px", borderTop: `1px solid ${D.line2}`, bgcolor: D.surface2, display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: D.ink3 }}>{selected.size} class{selected.size !== 1 ? "es" : ""} selected</Box>
        <Box sx={{ display: "flex", gap: "9px" }}>
          <Btn ghost sm onClick={onClose}>Cancel</Btn>
          <Btn primary sm onClick={handleConfirm} disabled={!selected.size || assigning} icon={assigning ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <Ico size={13} sw={2.5}><polyline points="20 6 9 17 4 12"/></Ico>}>
            Assign Selected
          </Btn>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── MAIN PAGE ────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
export default function SkillSheetDetailPage() {
  const D = useD();
  const { id } = useParams();
  const navigate = useNavigate();
  const { effectiveUser } = useTenant();
  const CATEGORIES = useConfigList("SKILL_CATEGORY", DEFAULT_CATEGORIES);

  const [sheet, setSheet] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, v] = await Promise.all([
        fetchSkillSheetBankItem(id),
        fetchSkillSheetBankAssignments(id).catch(() => []),
        fetchSkillSheetBankVersions(id).catch(() => []),
      ]);
      setSheet(s);
      setAssignments(Array.isArray(a) ? a : []);
      setVersions(Array.isArray(v) ? v : []);
    } catch { setErr("Failed to load skill sheet."); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = async (formData) => {
    const payload = {
      title: formData.title,
      description: formData.description,
      passingScore: formData.passingScore ?? null,
      timeLimitMin: formData.timeLimitMin ?? null,
      scenarioContext: formData.scenarioContext,
      items: JSON.stringify(formData.items),
      equipment: formData.equipment,
      category: formData.category,
      tags: formData.tags,
      peerAssessmentRequired: formData.peerAssessmentRequired,
    };
    if (formData.changeNote !== undefined) payload.changeNote = formData.changeNote;
    if (formData.autosave) {
      // Auto-save updates the page in place. A reload would show a spinner and close the editor mid-edit.
      const updated = await updateSkillSheetBankItem(id, payload);
      setSheet((prev) => ({ ...prev, ...updated }));
      return { id: Number(id) };
    }
    await updateSkillSheetBankItem(id, payload);
    if (formData.requestedStatus === "PUBLISHED" && sheet.status !== "PUBLISHED") await publishSkillSheetBankItem(id);
    await load();
  };

  const handleUnassign = async (assignment) => {
    try {
      await unassignSkillSheetFromCourse(id, assignment.courseId);
      setAssignments(prev => prev.filter(a => a.assignmentId !== assignment.assignmentId));
    } catch { setErr("Failed to unassign."); }
  };

  const handleDuplicate = async () => {
    const payload = {
      title: `${sheet.title} (copy)`,
      description: sheet.description,
      passingScore: sheet.passingScore,
      scenarioContext: sheet.scenarioContext,
      items: sheet.items,
      tags: sheet.tags,
      equipment: sheet.equipment,
      category: sheet.category,
      timeLimitMin: sheet.timeLimitMin,
      peerAssessmentRequired: sheet.peerAssessmentRequired,
    };
    try {
      const copy = await createSkillSheetBankItem(payload);
      navigate(`/instructor/knowledge-bank/skill-sheets/${copy.id}`);
    } catch { setErr("Failed to duplicate."); }
  };

  const handlePrint = () => {
    // Group items by section
    const sections = [];
    let stepNum = 0;
    items.forEach(item => {
      const title = item.sectionTitle || "General Skills";
      let sec = sections.find(s => s.title === title);
      if (!sec) { sec = { title, items: [] }; sections.push(sec); }
      sec.items.push({ ...item, _num: ++stepNum });
    });
    const multiSection = sections.length > 1;

    const tableBody = sections.map(sec => {
      const sectionHeader = multiSection
        ? `<tr><td colspan="4" style="padding:10px 8px 4px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:${D.primary};border-bottom:2px solid ${D.primary};">${sec.title}</td></tr>`
        : '';
      const rows = sec.items.map(item => `
        <tr>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;width:28px;color:var(--color-ink-3);">${item._num}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;">
            ${item.isCritical ? '<span style="color:#B42318;font-size:10px;font-weight:700;margin-right:4px;">★ CRIT</span>' : ''}${item.criteria || item.name || ''}
          </td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;text-align:center;width:60px;">☐</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:13px;text-align:center;width:60px;">☐</td>
        </tr>`).join('');
      return sectionHeader + rows;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>${sheet.title}</title>
      <style>body{font-family:Arial,sans-serif;margin:24px;color:#111;}h1{font-size:17px;margin-bottom:2px;}
      .meta{font-size:11px;color:#555;margin-bottom:14px;}table{width:100%;border-collapse:collapse;}
      th{font-size:11px;text-transform:uppercase;color:var(--color-ink-2);font-weight:700;padding:5px 8px;text-align:left;background:var(--color-surface-2);}
      .info{display:flex;gap:24px;margin-bottom:18px;flex-wrap:wrap;}
      .field{flex:1 1 160px;}.field label{font-size:10px;text-transform:uppercase;color:var(--color-ink-3);font-weight:700;display:block;}
      .line{border-bottom:1px solid var(--color-ink-2);height:20px;margin-top:2px;}
      @media print{body{margin:12px;}}</style></head><body>
      <h1>${sheet.title}</h1>
      <p class="meta">${sheet.description || ''}</p>
      <div class="info">
        <div class="field"><label>Candidate Name</label><div class="line"></div></div>
        <div class="field"><label>Examiner</label><div class="line"></div></div>
        <div class="field"><label>Date</label><div class="line"></div></div>
      </div>
      <table><thead><tr><th>#</th><th>Performance Criteria</th><th style="text-align:center">Pass</th><th style="text-align:center">Fail</th></tr></thead>
      <tbody>${tableBody}</tbody></table>
      ${sheet.passingScore != null ? `<p style="font-size:12px;margin-top:14px;">Passing score: ${sheet.passingScore} pts · ${sheet.timeLimitMin ? `Time limit: ${sheet.timeLimitMin} min` : 'No time limit'}</p>` : ''}
      </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const handleCategoryChange = async (newCat) => {
    setSavingCategory(true);
    try {
      const otherTags = tags.filter(t => !CATEGORIES.includes(t));
      const newTags = newCat ? [newCat, ...otherTags] : otherTags;
      await updateSkillSheetBankItem(id, { tags: JSON.stringify(newTags) });
      await load();
    } catch { setErr("Failed to update category."); }
    finally { setSavingCategory(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteSkillSheetBankItem(id);
      navigate("/instructor/knowledge-bank");
    } catch (e) {
      setErr(e?.response?.data?.message || "Cannot delete — may be assigned to courses.");
      setDeleteConfirm(false);
    } finally { setDeleting(false); }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}><CircularProgress sx={{ color: D.primary }} /></Box>;
  if (err && !sheet) return <Box sx={{ maxWidth: 600, mx: "auto", pt: 6 }}><Alert severity="error">{err}</Alert></Box>;
  if (!sheet) return null;

  const tags = parseTags(sheet.tags);
  const items = parseItems(sheet.items);
  const currentCategory = tags.find(t => CATEGORIES.includes(t)) || "";
  const { bg: iconBg, color: iconColor } = getIconStyle(tags, D.primary);
  const criticalCount = items.filter(i => i.isCritical).length;
  const totalPoints = items.reduce((s, i) => s + itemPoints(i), 0);

  // Hydrate initial for SkillSheetBuilderModal
  const builderInitial = sheet ? {
    id: sheet.id,
    title: sheet.title || "",
    description: sheet.description || "",
    passingScore: sheet.passingScore ?? "",
    timeLimitMin: sheet.timeLimitMin ?? "",
    scenarioContext: sheet.scenarioContext || "",
    tags: sheet.tags,
    status: sheet.status,
    equipment: sheet.equipment,
    category: sheet.category,
    peerAssessmentRequired: sheet.peerAssessmentRequired,
    items,
  } : null;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}

      {/* Back link */}
      <Box component="button" onClick={() => navigate("/instructor/knowledge-bank")}
        sx={{ display: "inline-flex", alignItems: "center", gap: "7px", fontSize: "13px", fontWeight: 600, color: D.ink3, border: 0, bgcolor: "transparent", cursor: "pointer", mb: "16px", p: 0, "&:hover": { color: D.primary } }}>
        <Ico size={15} sw={2.2}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></Ico>
        Back to Knowledge Bank
      </Box>

      {/* Hero */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "18px", mb: "20px", flexWrap: "wrap" }}>
        <Box sx={{ width: 56, height: 56, borderRadius: "15px", bgcolor: iconBg, color: iconColor, display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Ico size={26}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></Ico>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "28px", fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1.15, color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>{sheet.title}</Typography>
          {sheet.description && <Box sx={{ fontSize: "14.5px", color: D.ink3, mt: "6px", maxWidth: 560 }}>{sheet.description}</Box>}
          {tags.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap", mt: "11px" }}>
              {tags.map(t => <Tag key={t} label={t} />)}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0, flexWrap: "wrap" }}>
          <IconBtn title="Duplicate" onClick={handleDuplicate}>
            <Ico size={15}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></Ico>
          </IconBtn>
          <IconBtn title="Print" onClick={handlePrint}>
            <Ico size={15}><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></Ico>
          </IconBtn>
          <Btn secondary sm onClick={() => setEditOpen(true)} icon={<Ico size={14}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Ico>}>
            Edit Sheet
          </Btn>
          <Btn primary sm onClick={() => setAssignOpen(true)} icon={<Ico size={14}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>}>
            Assign to Class
          </Btn>
        </Box>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" }, gap: "20px", alignItems: "start" }}>

        {/* Main column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>

          {/* Scenario Context */}
          {sheet.scenarioContext && (
            <Panel>
              <PanelHead
                icon={<Ico size={15}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Ico>}
                iconBg={D.surface3} iconColor={D.ink3}
                title="Scenario Context"
              />
              <Box sx={{ p: "16px 20px" }}>
                <Box sx={{ fontSize: "13.5px", color: D.ink2, lineHeight: 1.55 }}>{sheet.scenarioContext}</Box>
              </Box>
            </Panel>
          )}

          {/* Equipment */}
          {sheet.equipment && (
            <Panel>
              <PanelHead
                icon={<Ico size={15}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></Ico>}
                iconBg={D.surface3} iconColor={D.ink3}
                title="Equipment"
              />
              <Box sx={{ p: "16px 20px" }}>
                <Box sx={{ fontSize: "13.5px", color: D.ink2, lineHeight: 1.55 }}>{sheet.equipment}</Box>
              </Box>
            </Panel>
          )}

          {/* Evaluation Checklist */}
          <Panel>
            <PanelHead
              icon={<Ico size={15}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></Ico>}
              title="Evaluation Checklist"
              badge={`${items.length} step${items.length !== 1 ? "s" : ""}`}
              action={<Btn secondary sm onClick={() => setEditOpen(true)} icon={<Ico size={13}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Ico>}>Edit Steps</Btn>}
            />
            {criticalCount > 0 && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "11px", borderRadius: "12px", p: "13px 15px", m: "0 20px 0", mt: "14px", fontSize: "13px", lineHeight: 1.55, bgcolor: D.danger50, color: D.ink2, border: `1px solid ${D.danger100}` }}>
                <Ico size={16} style={{ color: D.danger, flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Ico>
                Steps marked <Box component="strong" sx={{ color: D.danger, mx: "3px" }}>Critical</Box> are automatic-fail if missed.
              </Box>
            )}
            <Box sx={{ p: "14px 20px", display: "flex", flexDirection: "column", gap: "2px" }}>
              {items.length === 0 ? (
                <Box sx={{ textAlign: "center", py: "24px", color: D.ink4, fontSize: "13.5px" }}>No steps defined. Click "Edit Steps" to add criteria.</Box>
              ) : (() => {
                // Group items by sectionTitle, preserving order
                const sections = [];
                let stepNum = 0;
                items.forEach(item => {
                  const title = item.sectionTitle || "General Skills";
                  let sec = sections.find(s => s.title === title);
                  if (!sec) { sec = { title, items: [] }; sections.push(sec); }
                  sec.items.push({ ...item, _num: ++stepNum });
                });
                const multiSection = sections.length > 1;
                return sections.map((sec, secIdx) => (
                  <Box key={sec.title} sx={secIdx > 0 ? { mt: "8px", pt: "4px" } : {}}>
                    {multiSection && (
                      <Box sx={{ fontSize: "11px", fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: D.primary, px: "12px", pt: "10px", pb: "4px", display: "flex", alignItems: "center", gap: "8px",
                        "&::after": { content: '""', flex: 1, height: "1px", bgcolor: D.primary10 } }}>
                        {sec.title}
                      </Box>
                    )}
                    {sec.items.map((item) => (
                      <Box key={item._num} sx={{ display: "flex", alignItems: "flex-start", gap: "13px", p: "12px", borderRadius: "11px", transition: "background .12s", "&:hover": { bgcolor: D.surface2 } }}>
                        <Box sx={{ width: 26, height: 26, borderRadius: "8px", flexShrink: 0, bgcolor: D.primary10, color: D.primary, display: "grid", placeItems: "center", fontSize: "12.5px", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                          {item._num}
                        </Box>
                        <Box sx={{ flex: 1, fontSize: "14px", color: D.ink, lineHeight: 1.5, pt: "2px" }}>
                          {item.criteria || item.name || item.text || "—"}
                          {item.isCritical && (
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "10.5px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: D.danger, bgcolor: D.danger50, border: `1px solid ${D.danger100}`, px: "7px", py: "1px", borderRadius: "999px", ml: "8px", verticalAlign: "middle" }}>
                              <Ico size={9} sw={2.5}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></Ico>
                              Critical
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ));
              })()}
            </Box>
          </Panel>

          {/* Scoring & Evaluation */}
          <Panel>
            <PanelHead
              icon={<Ico size={15}><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></Ico>}
              iconBg={D.amber50} iconColor={D.amber}
              title="Scoring & Evaluation"
            />
            <Box sx={{ p: "10px 20px 18px" }}>
              <SetRow label="Category" sub="Used for filtering in the Knowledge Bank"
                value={
                  <Select size="small" value={currentCategory} displayEmpty disabled={savingCategory}
                    onChange={e => handleCategoryChange(e.target.value)}
                    sx={{ height: 32, fontSize: "12.5px", fontWeight: 700, borderRadius: "999px", "& fieldset": { borderColor: D.line }, "& .MuiSelect-select": { py: "4px", px: "11px" }, minWidth: 140 }}>
                    <MenuItem value="" sx={{ fontSize: "13px" }}><em style={{ fontStyle: "normal", color: D.ink3 }}>None</em></MenuItem>
                    {["Firefighter","EMS","Hazmat","Search & Rescue","Fire Officer","General"].map(c => (
                      <MenuItem key={c} value={c} sx={{ fontSize: "13px" }}>{c}</MenuItem>
                    ))}
                  </Select>
                } />
              <SetRow label="Passing Threshold" sub="Points needed to pass"
                value={<Pill green>{sheet.passingScore != null ? `${sheet.passingScore} pts` : `${totalPoints} pts — all steps`}</Pill>} />
              <SetRow label="Time Limit" sub="Maximum allowed completion time"
                value={<Pill live>{sheet.timeLimitMin ? `${sheet.timeLimitMin} min` : "No limit"}</Pill>} />
              <SetRow label="Critical Steps" sub="Auto-fail if any critical step is missed"
                value={<Pill>{criticalCount} critical</Pill>} />
              <SetRow label="Total Points Possible" sub="Sum of all step point values"
                value={<Pill>{totalPoints} pts</Pill>} />
            </Box>
          </Panel>
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", position: { lg: "sticky" }, top: { lg: "76px" } }}>

          {/* Assigned Classes */}
          <Panel lime>
            <PanelHead
                icon={<Ico size={15}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>}
                title="Assigned Classes"
                badge={assignments.length}
                tint
            />
            <Box sx={{ p: "10px 20px 14px" }}>
              {assignments.length === 0 ? (
                <Box sx={{ textAlign: "center", color: D.ink3, fontSize: "13px", py: "8px 0 4px" }}>Not assigned to any classes yet.</Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {assignments.map(a => (
                    <Box key={a.assignmentId} sx={{ display: "flex", alignItems: "center", gap: "13px", py: "13px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: "2px" }, "&:first-of-type": { pt: "2px" } }}>
                      <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: D.live50, color: D.live, display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <Ico size={18}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Ico>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: "14px", fontWeight: 700, color: D.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.courseTitle}</Box>
                        <Box sx={{ fontSize: "12.5px", color: D.ink3, mt: "1px", display: "flex", alignItems: "center", gap: "8px" }}>
                          {a.courseNumber && <Box sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "11.5px" }}>{a.courseNumber}</Box>}
                        </Box>
                      </Box>
                      <IconBtn dangerHover title="Unassign" onClick={() => handleUnassign(a)}>
                        <Ico size={13}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>
                      </IconBtn>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Panel>

          {/* Details */}
          <Panel>
            <PanelHead icon={<Ico size={15}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Ico>} iconBg={D.surface3} iconColor={D.ink3} title="Details" />
            <Box sx={{ p: "10px 20px 18px" }}>
              <MetaRow icon={<Ico size={13}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></Ico>} label="Created by" value={effectiveUser?.fullName || effectiveUser?.name || "You"} />
              <MetaRow icon={<Ico size={13}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Ico>} label="Created" value={fmtDate(sheet.createdAt)} />
              <MetaRow icon={<Ico size={13}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></Ico>} label="Last edited" value={fmtDate(sheet.updatedAt)} />
              <MetaRow icon={<Ico size={13}><path d="M9 11l3 3L22 4"/></Ico>} label="Total steps" value={`${items.length} steps`} />
              <MetaRow icon={<Ico size={13}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></Ico>} label="Time limit" value={sheet.timeLimitMin ? `${sheet.timeLimitMin} min` : "None"} />
            </Box>
          </Panel>

          {/* Version History */}
          <Panel>
            <PanelHead icon={<Ico size={15}><path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"/><path d="M12 8v4l3 3"/></Ico>} iconBg={D.surface3} iconColor={D.ink3} title="Version History" badge={versions.length || null} />
            <Box sx={{ p: "10px 20px 14px" }}>
              {versions.length === 0 ? (
                <Box sx={{ textAlign: "center", color: D.ink3, fontSize: "13px", py: "8px 0 4px" }}>No version history yet.</Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {versions.map((v, idx) => (
                    <Box key={v.id} sx={{ display: "flex", alignItems: "flex-start", gap: "12px", py: "13px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: "2px" }, "&:first-of-type": { pt: "2px" } }}>
                      <Box sx={{
                        width: 24, height: 24, borderRadius: "50%", flexShrink: 0, mt: "1px",
                        display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 800,
                        bgcolor: idx === 0 ? D.primary : D.surface3,
                        color: idx === 0 ? "var(--color-surface-canvas-light)" : D.ink3,
                      }}>
                        {v.versionNumber}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: "13.5px", fontWeight: 700, color: D.ink }}>
                          v{v.versionNumber} — {v.changeNote || "Updated skill sheet"}
                        </Box>
                        <Box sx={{ fontSize: "12px", color: D.ink3, mt: "2px" }}>
                          {(v.createdByName || "Unknown")} · {fmtDate(v.createdAt)}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Panel>

          {/* Danger zone */}
          <Panel>
            <Box sx={{ p: "18px 20px", display: "flex", flexDirection: "column", gap: "9px" }}>
              <Btn secondary block icon={<Ico size={14}><path d="M21 8v13H3V8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></Ico>}>
                Archive Skill Sheet
              </Btn>
              <Btn danger block onClick={() => setDeleteConfirm(true)} icon={<Ico size={14}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></Ico>}>
                Delete Skill Sheet
              </Btn>
            </Box>
          </Panel>
        </Box>
      </Box>

      {/* Edit modal */}
      <SkillSheetBuilderModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        bankMode
        onSave={handleEdit}
        initial={builderInitial}
      />

      {/* Assign to Class modal */}
      <AssignModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        sheetId={id}
        alreadyAssigned={assignments}
        onAssigned={() => fetchSkillSheetBankAssignments(id).then(d => setAssignments(Array.isArray(d) ? d : []))}
      />

      {/* Delete confirm */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontSize: "17px", fontWeight: 800, color: D.ink }}>Delete Skill Sheet?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "13.5px", color: D.ink3 }}>
            This will permanently delete "{sheet.title}". This cannot be undone. Skill sheets assigned to courses must be unassigned first.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: "12px 24px 18px", gap: "8px" }}>
          <Btn ghost sm onClick={() => setDeleteConfirm(false)}>Cancel</Btn>
          <Btn danger sm onClick={handleDelete} disabled={deleting} icon={deleting ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : null}>
            {deleting ? "Deleting…" : "Delete"}
          </Btn>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
